import ImmutableUnderReviewCompanyFormCheck from '@rd-web-markets/shared/dist/util/reviewEditControls/ImmutableUnderReviewCompanyFormCheck';
import ImmutableUnderReviewCompanyFormControl from '@rd-web-markets/shared/dist/util/reviewEditControls/ImmutableUnderReviewCompanyFormControl';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import sicCodeService from '@rd-web-markets/shared/dist/services/sic_codes/sic_code.service';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import sicCodeSectionService from '@rd-web-markets/shared/dist/services/sic_codes/sic_code_section.service';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { DASHBOARD_TYPES } from '../constants';
import { Button, EditButton, DeleteButton, SaveButton, CancelButton } from '@rd-web-markets/shared/dist/util/buttons';
import { Table } from '@rd-web-markets/shared/dist/util';
import subsidiaryCompanyService from '@rd-web-markets/shared/dist/services/subsidiary_company.service';
import CenteredFormRow from './CenteredFormRow';
const CompanyMarketRelatedFields = _ref => {
  let {
    company,
    handleChange,
    setCompany
  } = _ref;
  const user = useSelector(state => state.auth.user);
  const history = useHistory();
  const {
    t
  } = useTranslation();
  const [sicCodes, setSicCodes] = useState([]);
  const [sicCodeSections, setSicCodeSections] = useState([]);
  const [subsidiaryCompanies, setSubsidiaryCompanies] = useState(null);
  const [subsidiaryCompanyName, setSubsidiaryCompanyName] = useState('');
  const [subsidiaryCompanyUtr, setSubsidiaryCompanyUtr] = useState('');
  const [newSubsidiaryCompany, setNewSubsidiaryCompany] = useState({});
  const [editingSubsidiaryId, setEditingSubsidiaryId] = useState(null);
  const [editingFormData, setEditingFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useState();
  const updateSubsidiaryCompany = subsidiaryCompanyService.useUpdateSubsidiaryCompany(company.id);
  const sicCodeOptions = sicCodes.map(s => ({
    value: s.id,
    label: `(${s.number}) ${s.title}`,
    sic_code_section_id: s.sic_code_section_id
  }));
  const sicCodeSectionOptions = sicCodeSections.map(s => ({
    value: s.id,
    label: s.name
  }));
  const dashboardTypeOptions = DASHBOARD_TYPES.map(type => ({
    value: type,
    label: t(type)
  }));
  const getSicCodes = useErrorHandling(useCallback(async () => {
    const response = await sicCodeService.get(params);
    setSicCodes(response.sic_codes);
  }));
  const getSicCodeSections = useErrorHandling(useCallback(async () => {
    const response = await sicCodeSectionService.get();
    setSicCodeSections(response.sic_code_sections);
  }, [setSicCodeSections]));
  useEffect(() => {
    getSicCodes();
    if (company.id) {
      const getSubsidiaryCompanies = async () => {
        const subsidiaryCompanies = await subsidiaryCompanyService.all(company.id);
        setSubsidiaryCompanies(subsidiaryCompanies);
      };
      getSubsidiaryCompanies();
    }
  }, [history, params, company.id]);
  useEffect(() => {
    getSicCodeSections();
  }, []);
  const handleSicCodeDropdownChange = e => {
    setCompany({
      ...company,
      sic_code_id: e.value,
      sic_code_section: e.sic_code_section_id
    });
  };
  const handleSicCodeSectionDropdownChange = e => {
    setCompany({
      ...company,
      sic_code_section: e.value
    });
    setParams(`by_section=${e.value}`);
  };
  const handleDashboardTypeDropdownChange = e => {
    setCompany({
      ...company,
      dashboard_type: e.value
    });
  };
  const handleNewSubsidiaryCompanyChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setNewSubsidiaryCompany(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleEditingFormDataChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setEditingFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const createSubsidiaryCompany = async () => {
    const response = await subsidiaryCompanyService.create(company.id, newSubsidiaryCompany);
    setSubsidiaryCompanies([...subsidiaryCompanies, response]);
    setNewSubsidiaryCompany({});
  };
  const handleEditStart = (index, subsidiary) => {
    setEditingSubsidiaryId(subsidiary.id);
    setEditingFormData({
      ...subsidiary,
      sic_code_section: subsidiary.sic_code?.sic_code_section?.id ?? subsidiary.sic_code_section
    });
  };
  const handleEditCancel = () => {
    setEditingSubsidiaryId(null);
    setEditingFormData({});
  };
  const handleEditSave = async () => {
    setSaving(true);
    const updatedSubsidiary = await updateSubsidiaryCompany({
      ...editingFormData
    });
    setSubsidiaryCompanies(prev => prev.map(s => s.id === updatedSubsidiary.id ? updatedSubsidiary : s));
    setEditingSubsidiaryId(null);
    setEditingFormData({});
    setSaving(false);
  };
  const removeSubsidiaryCompany = async subsidiary => {
    if (!window.confirm('Are you sure you want to delete this subsidiary company?')) return;
    await subsidiaryCompanyService.destroy(company.id, subsidiary.id);
    setSubsidiaryCompanies(prev => prev.filter(sc => sc.id !== subsidiary.id));
    setSubsidiaryCompanyName('');
    setSubsidiaryCompanyUtr('');
  };
  const handleNewSubsidiarySicCodeDropdownChange = e => {
    setNewSubsidiaryCompany(prev => ({
      ...prev,
      sic_code_id: e.value,
      sic_code_section: e.sic_code_section_id
    }));
  };
  const handleNewSubsidiarySicSectionDropdownChange = e => {
    setNewSubsidiaryCompany(prev => ({
      ...prev,
      sic_code_section: e.value
    }));
    setParams(`by_section=${e.value}`);
  };
  const handleEditSicCodeDropdownChange = e => {
    setEditingFormData(prev => ({
      ...prev,
      sic_code_id: e.value,
      sic_code_section: e.sic_code_section_id
    }));
  };
  const handleEditSicSectionDropdownChange = e => {
    setEditingFormData(prev => ({
      ...prev,
      sic_code_section: e.value
    }));
    setParams(`by_section=${e.value}`);
  };
  const handleImageUpload = async event => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        setCompany({
          ...company,
          image_data: base64String
        });
      };
      reader.readAsDataURL(file);
    }
  };
  const subsidiaryTableHeaders = [{
    text: 'Subsidiary Name'
  }, {
    text: 'Company Number'
  }, {
    text: 'UTR'
  }, {
    text: 'VAT Number'
  }, {
    text: 'Has Paye Ref Number'
  }, {
    text: 'PAYE Number'
  }, {
    text: 'SIC Code Section'
  }, {
    text: 'Industry/Sector'
  }, {
    text: ''
  }];
  const subsidiaryTableRows = useMemo(() => {
    if (!subsidiaryCompanies) return [];
    return subsidiaryCompanies.map((subsidiary, index) => {
      const isEditing = editingSubsidiaryId === subsidiary.id;
      const isSaving = saving && isEditing;
      const columns = isEditing ? [/*#__PURE__*/React.createElement(Form.Control, {
        size: "sm",
        name: "name",
        "data-testid": "edit-subsidiary-name",
        value: editingFormData.name || '',
        onChange: handleEditingFormDataChange
      }), /*#__PURE__*/React.createElement(Form.Control, {
        size: "sm",
        name: "number",
        "data-testid": "edit-subsidiary-number",
        value: editingFormData.number || '',
        onChange: handleEditingFormDataChange
      }), /*#__PURE__*/React.createElement(Form.Control, {
        size: "sm",
        name: "utr",
        "data-testid": "edit-subsidiary-utr",
        value: editingFormData.utr || '',
        onChange: handleEditingFormDataChange
      }), /*#__PURE__*/React.createElement(Form.Control, {
        size: "sm",
        name: "vat_number",
        "data-testid": "edit-subsidiary-vat-number",
        value: editingFormData.vat_number || '',
        onChange: handleEditingFormDataChange
      }), /*#__PURE__*/React.createElement(Form.Check, {
        name: "has_paye_ref_number",
        type: "checkbox",
        "data-testid": "edit-subsidiary-has-paye-ref-number",
        checked: editingFormData.has_paye_ref_number || false,
        onChange: handleEditingFormDataChange
      }), /*#__PURE__*/React.createElement(Form.Control, {
        size: "sm",
        name: "paye_ref_number",
        "data-testid": "edit-subsidiary-paye-ref-number",
        value: editingFormData.paye_ref_number || '',
        onChange: handleEditingFormDataChange
      }), /*#__PURE__*/React.createElement("div", {
        "data-testid": "edit-subsidiary-sic-section"
      }, /*#__PURE__*/React.createElement(Select, {
        value: sicCodeSectionOptions.find(o => o.value === editingFormData.sic_code_section),
        onChange: handleEditSicSectionDropdownChange,
        options: sicCodeSectionOptions,
        styles: {
          control: baseStyles => ({
            ...baseStyles,
            borderColor: '#80bbdc'
          })
        }
      })), /*#__PURE__*/React.createElement("div", {
        "data-testid": "edit-subsidiary-sic-code"
      }, /*#__PURE__*/React.createElement(Select, {
        value: sicCodeOptions.find(o => o.value === editingFormData.sic_code_id),
        onChange: handleEditSicCodeDropdownChange,
        options: sicCodeOptions,
        styles: {
          control: baseStyles => ({
            ...baseStyles,
            borderColor: '#80bbdc'
          })
        }
      })), /*#__PURE__*/React.createElement("div", {
        className: "d-flex justify-content-end gap-2"
      }, /*#__PURE__*/React.createElement(SaveButton, {
        size: "sm",
        onClick: handleEditSave,
        disabled: isSaving,
        text: isSaving ? t('saving') : t('save')
      }), /*#__PURE__*/React.createElement(CancelButton, {
        size: "sm",
        onClick: handleEditCancel,
        disabled: isSaving
      }))] : [subsidiary.name, subsidiary.number, subsidiary.utr, subsidiary.vat_number, subsidiary.has_paye_ref_number ? 'Yes' : 'No', subsidiary.paye_ref_number, subsidiary.sic_code ? subsidiary.sic_code.sic_code_section.name : '', subsidiary.sic_code ? subsidiary.sic_code.title : '', /*#__PURE__*/React.createElement("div", {
        className: "d-flex justify-content-end gap-2"
      }, /*#__PURE__*/React.createElement(EditButton, {
        size: "sm",
        onClick: () => handleEditStart(index, subsidiary)
      }), /*#__PURE__*/React.createElement(DeleteButton, {
        size: "sm",
        onClick: () => removeSubsidiaryCompany(subsidiary)
      }))];
      return {
        key: `subsidiary-${subsidiary.id}-${isEditing ? 'edit' : 'view'}`,
        data: {
          object: {
            ...subsidiary
          }
        },
        columns
      };
    });
  }, [subsidiaryCompanies, editingSubsidiaryId, editingFormData, saving, sicCodeOptions, sicCodeSectionOptions]);
  return /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    md: 12
  }, /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Company Name*"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: company.name,
    name: "name",
    disabled: user.account_type !== 'admin',
    onChange: handleChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Company Abbreviated Name"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: company.abbreviated_name || '',
    name: "abbreviated_name",
    onChange: handleChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "VAT Number"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: company.vat_number || '',
    name: "vat_number",
    onChange: handleChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Has Paye Reference Number?"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "Yes",
    value: "true",
    checked: company.has_paye_ref_number?.toString() === 'true',
    name: "has_paye_ref_number",
    onChange: handleChange,
    size: "md"
  }), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "No",
    value: "false",
    checked: company.has_paye_ref_number?.toString() === 'false',
    name: "has_paye_ref_number",
    onChange: handleChange,
    size: "md"
  })), company.has_paye_ref_number?.toString() === 'true' && /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Paye Ref Number"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: company.paye_ref_number || '',
    name: "paye_ref_number",
    onChange: handleChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Claimed R&D tax credits before?*"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "Yes",
    value: "true",
    checked: company.has_made_claims_before?.toString() === 'true',
    name: "has_made_claims_before",
    onChange: handleChange,
    size: "md"
  }), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "No",
    value: "false",
    checked: company.has_made_claims_before?.toString() === 'false',
    name: "has_made_claims_before",
    onChange: handleChange,
    size: "md"
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Has the company accounting period changed?"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "Yes",
    value: "true",
    disabled: user.account_type !== 'admin',
    checked: company.accounts_period_changed?.toString() === 'true',
    name: "accounts_period_changed",
    onChange: handleChange,
    size: "md"
  }), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "No",
    value: "false",
    checked: company.accounts_period_changed?.toString() === 'false',
    name: "accounts_period_changed",
    onChange: handleChange,
    size: "md"
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Business Unit Required"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "Yes",
    value: "true",
    disabled: user.account_type !== 'admin',
    checked: company.business_unit_required?.toString() === 'true',
    name: "business_unit_required",
    onChange: handleChange,
    size: "md"
  }), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "No",
    value: "false",
    checked: company.business_unit_required?.toString() === 'false',
    name: "business_unit_required",
    onChange: handleChange,
    size: "md"
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Has Subsidiary Companies?"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "Yes",
    value: "true",
    disabled: user.account_type !== 'admin',
    checked: company.has_subsidiary_companies?.toString() === 'true',
    name: "has_subsidiary_companies",
    onChange: handleChange,
    size: "md"
  }), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "No",
    value: "false",
    checked: company.has_subsidiary_companies?.toString() === 'false',
    name: "has_subsidiary_companies",
    onChange: handleChange,
    size: "md"
  })), company.has_subsidiary_companies && /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Requires separate AIF submission?"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "Yes",
    value: "true",
    disabled: user.account_type !== 'admin',
    checked: company.requires_separate_aif_submission?.toString() === 'true',
    name: "requires_separate_aif_submission",
    onChange: handleChange,
    size: "md"
  }), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormCheck, {
    inline: true,
    type: "radio",
    label: "No",
    value: "false",
    checked: company.requires_separate_aif_submission?.toString() === 'false',
    name: "requires_separate_aif_submission",
    onChange: handleChange,
    size: "md"
  })), company.has_subsidiary_companies?.toString() === 'true' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("hr", {
    className: "m-4"
  }), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Col, {
    md: 12
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    className: "mt-4"
  }, "Enter new Subsidiary details")), /*#__PURE__*/React.createElement(Col, {
    md: 2
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Subsidiary Name"), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: newSubsidiaryCompany.name || '',
    name: "name",
    "data-testid": "new-subsidiary-name",
    onChange: handleNewSubsidiaryCompanyChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(Col, {
    md: 1
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Company Number"), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: newSubsidiaryCompany.number || '',
    name: "number",
    "data-testid": "new-subsidiary-number",
    onChange: handleNewSubsidiaryCompanyChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(Col, {
    md: 1
  }, /*#__PURE__*/React.createElement(Form.Label, null, "UTR"), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: newSubsidiaryCompany.utr || '',
    name: "utr",
    "data-testid": "new-subsidiary-utr",
    onChange: handleNewSubsidiaryCompanyChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(Col, {
    md: 1
  }, /*#__PURE__*/React.createElement(Form.Label, null, "VAT Number"), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: newSubsidiaryCompany.vat_number || '',
    name: "vat_number",
    "data-testid": "new-subsidiary-vat-number",
    onChange: handleNewSubsidiaryCompanyChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(Col, {
    md: 1,
    className: "d-flex flex-column align-items-center justify-content-center text-center"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Has PAYE Ref Number"), /*#__PURE__*/React.createElement(Form.Check, {
    name: "has_paye_ref_number",
    type: "checkbox",
    "data-testid": "new-subsidiary-has-paye-ref-number",
    checked: newSubsidiaryCompany.has_paye_ref_number || false,
    onChange: handleNewSubsidiaryCompanyChange
  })), /*#__PURE__*/React.createElement(Col, {
    md: 1
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Paye Ref Number"), /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: newSubsidiaryCompany.paye_ref_number || '',
    name: "paye_ref_number",
    "data-testid": "new-subsidiary-paye-ref-number",
    onChange: handleNewSubsidiaryCompanyChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(Col, {
    md: 2
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md"
  }, "SIC Code Section", /*#__PURE__*/React.createElement(OverlayTrigger, {
    placement: "right",
    overlay: /*#__PURE__*/React.createElement(Tooltip, null, "\"In case you don't know \"SIC Code Section\", find exact SIC code using field \"Industry/Sector (SIC)\"\"")
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18 ms-1",
    style: {
      color: '#0078b9'
    }
  }, "info"))), /*#__PURE__*/React.createElement("div", {
    "data-testid": "new-subsidiary-sic-section"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    as: Select,
    value: sicCodeSectionOptions.find(o => o.value === newSubsidiaryCompany.sic_code_section),
    onChange: handleNewSubsidiarySicSectionDropdownChange,
    options: sicCodeSectionOptions,
    styles: {
      control: baseStyles => ({
        ...baseStyles,
        borderColor: '#80bbdc'
      })
    }
  }))), /*#__PURE__*/React.createElement(Col, {
    md: 2
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md"
  }, "Industry/Sector (SIC)"), /*#__PURE__*/React.createElement("div", {
    "data-testid": "new-subsidiary-sic-code"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    as: Select,
    value: sicCodeOptions.find(o => o.value === newSubsidiaryCompany.sic_code_id),
    onChange: handleNewSubsidiarySicCodeDropdownChange,
    options: sicCodeOptions,
    styles: {
      control: baseStyles => ({
        ...baseStyles,
        borderColor: '#80bbdc'
      })
    }
  }))), /*#__PURE__*/React.createElement(Col, {
    md: 12
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    className: "mt-3"
  }), /*#__PURE__*/React.createElement(Button, {
    className: "ms-4 mt-1",
    onClick: createSubsidiaryCompany,
    size: "md"
  }, "+ Add New Subsidiary"))), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Col, {
    md: 12
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md"
  }, "Subsidiary Listing"), /*#__PURE__*/React.createElement(Table, {
    headers: subsidiaryTableHeaders,
    rows: subsidiaryTableRows,
    showControls: false
  }))), /*#__PURE__*/React.createElement("hr", {
    className: "m-4"
  })), company.has_subsidiary_companies?.toString() === 'false' && /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Company Unique Tax Reference (UTR)"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    value: company.utr || '',
    name: "utr",
    onChange: handleChange,
    size: "md",
    type: "text"
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: /*#__PURE__*/React.createElement(React.Fragment, null, "SIC Code Section", /*#__PURE__*/React.createElement(OverlayTrigger, {
      placement: "right",
      overlay: /*#__PURE__*/React.createElement(Tooltip, null, "\"In case you don't know \"SIC Code Section\", find exact SIC code using field \"Industry/Sector (SIC)\"\"")
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons md-18 ms-1",
      style: {
        color: '#0078b9'
      }
    }, "info")))
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    as: Select,
    value: sicCodeSectionOptions.find(o => o.value === company.sic_code_section),
    onChange: handleSicCodeSectionDropdownChange,
    options: sicCodeSectionOptions,
    styles: {
      control: baseStyles => ({
        ...baseStyles,
        borderColor: '#80bbdc'
      })
    }
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Industry/Sector (SIC)"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    as: Select,
    value: sicCodeOptions.find(o => o.value === company.sic_code_id),
    onChange: handleSicCodeDropdownChange,
    options: sicCodeOptions,
    styles: {
      control: baseStyles => ({
        ...baseStyles,
        borderColor: '#80bbdc'
      })
    }
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: "Dashboard Type"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewCompanyFormControl, {
    as: Select,
    value: dashboardTypeOptions.find(o => o.value === company.dashboard_type),
    onChange: handleDashboardTypeDropdownChange,
    options: dashboardTypeOptions,
    styles: {
      control: baseStyles => ({
        ...baseStyles,
        borderColor: '#80bbdc'
      })
    }
  })), /*#__PURE__*/React.createElement(CenteredFormRow, {
    label: t('company_logo')
  }, /*#__PURE__*/React.createElement("input", {
    type: "file",
    accept: "image/*",
    onChange: handleImageUpload,
    className: "form-control"
  }), company.image_data && /*#__PURE__*/React.createElement("img", {
    src: `data:image/png;base64,${company.image_data}`,
    alt: "Template Preview",
    style: {
      maxWidth: '200px',
      marginTop: '10px'
    }
  }))));
};
export default CompanyMarketRelatedFields;